import { formatNameLastFirst } from '@strata/helper-functions';
import { Dialog } from '@strata/react-dialog';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { TextArea } from '@strata/react-inputs';
import { useFormikContext } from 'formik';
import { useState } from 'react';

import { type PrescriberListPrescriber } from 'api/auctionsAPI';
import { getBulkPrescriberInfo } from 'api/referenceAPI';
import { handleError } from 'services/Error';

interface AddPrescribersDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

export const AddPrescribersDialog = ({ isOpen, onClose }: AddPrescribersDialogProps) => {
    const formik = useFormikContext<{ prescribers?: PrescriberListPrescriber[] }>();

    const [npis, setNpis] = useState<string>();
    const [checkingNpis, setCheckingNpis] = useState(false);
    const [validationMsg, setValidationMsg] = useState<string>();

    const reset = () => {
        setNpis(undefined);
        setValidationMsg(undefined);
    };

    return (
        <>
            <FullscreenLoading
                isShowing={checkingNpis}
                loadingText="Validating prescriber NPIs..."
            />
            <Dialog
                dialogTitle="Add Prescribers"
                primaryButtonLabel="Add Prescribers"
                isPrimaryButtonDisabled={!npis || checkingNpis}
                // Primary button starts disabled; auto-focusing it on open steals focus from the NPI input mid-typing
                focusPrimaryButton={false}
                onPrimaryButtonClick={() => {
                    void (async () => {
                        try {
                            setCheckingNpis(true);

                            // Check for invalid characters
                            if (/[^\d\s,]/.test(npis ?? '')) {
                                setValidationMsg('NPI list formatted incorrectly.');
                                return;
                            }

                            // Get array of existing npis
                            const existingNpis =
                                formik.values.prescribers?.map(({ npi }) => npi) ?? [];

                            // Split npi list on newlines and commas, dedupe list w/ set
                            const npisToAdd = [...new Set(npis?.split(/[\r\n,]/))]
                                // Trim whitespace
                                .map((npi) => npi.trim())
                                // Filter out npis already in list and empty strings
                                .filter((npi) => npi && !existingNpis.includes(npi));

                            // Lookup prescribers
                            const response = await getBulkPrescriberInfo(npisToAdd);
                            const prescribersToAdd = response?.prescribers ?? [];

                            // Check to see if any NPIs are not valid Type 1 NPIs
                            const invalidNpis = [
                                ...(response?.not_found ?? []),
                                ...prescribersToAdd
                                    .filter((prescriber) => !prescriber.first_name)
                                    .map((prescriber) => prescriber.prescriber_npi),
                            ];

                            if (invalidNpis.length > 0) {
                                setValidationMsg(
                                    `The following NPIs are not Type 1 NPIs. Please correct them and try again: ${invalidNpis.join(
                                        ', ',
                                    )}`,
                                );
                                return;
                            }

                            // Add new prescribers to list
                            await formik.setFieldValue('prescribers', [
                                ...(formik.values.prescribers ?? []),
                                ...prescribersToAdd.map((prescriber) => ({
                                    npi: prescriber.prescriber_npi,
                                    name: formatNameLastFirst(
                                        prescriber.last_name,
                                        prescriber.first_name,
                                    ),
                                })),
                            ]);

                            onClose();
                            reset();
                        } catch (err) {
                            handleError('Error adding prescribers.', err as Error);
                        } finally {
                            setCheckingNpis(false);
                        }
                    })();
                }}
                secondaryButtonLabel="Cancel"
                onSecondaryButtonClick={() => {
                    onClose();
                    reset();
                }}
                isOpen={isOpen}
                width={700}
                dialogContent={
                    <>
                        <div className="margin__bottom--large">
                            Add prescribers by entering their NPI number below.
                        </div>
                        <TextArea
                            id="npis"
                            name="npis"
                            label="NPI"
                            helperText="Separate multiple NPI numbers using a comma (e.g., 0000000000,1111111111) or insert one NPI number per line."
                            required
                            value={npis}
                            onChange={(e) => setNpis(e.target.value)}
                            requiredValidationMessage={validationMsg}
                        />
                    </>
                }
            />
        </>
    );
};
